import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { superAdminApi } from '../../lib/superAdminApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Palette, Save, Upload, ArrowLeft, Eye, Shield } from 'lucide-react';
import { toast } from 'sonner';

const FONTS = [
  { v: 'Inter, sans-serif', l: 'Inter (default)' },
  { v: 'system-ui, sans-serif', l: 'System UI' },
  { v: 'Georgia, serif', l: 'Georgia (serif)' },
  { v: 'JetBrains Mono, monospace', l: 'JetBrains Mono' },
  { v: '"Poppins", sans-serif', l: 'Poppins' },
  { v: '"Playfair Display", serif', l: 'Playfair Display' },
];

const DEFAULT_BRANDING = {
  primary_color: '#2196C9',
  accent_color: '#5BB8E8',
  background_color: '#0a0e1a',
  font_family: 'Inter, sans-serif',
  tagline: 'Compliance made simple',
  logo_base64: '',
  custom_domain: '',
  login_hero_image: '',
};

export default function SuperAdminBranding() {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    superAdminApi.getBranding(tenantId).then((r) => {
      setTenant(r.data.tenant);
      setBranding({ ...DEFAULT_BRANDING, ...(r.data.branding || {}) });
    }).finally(() => setLoading(false));
  }, [tenantId]);

  const patch = (k, v) => setBranding((b) => ({ ...b, [k]: v }));

  const uploadLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error('Logo demasiado grande — máx 500KB'); return; }
    const reader = new FileReader();
    reader.onload = () => patch('logo_base64', reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await superAdminApi.updateBranding(tenantId, branding);
      toast.success('Branding actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao guardar');
    } finally { setSaving(false); }
  };

  const resetDefaults = () => setBranding(DEFAULT_BRANDING);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#5BB8E8' }} /></div>;

  return (
    <div className="space-y-6" data-testid="sa-branding-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => nav('/super-admin/tenants')} className="text-white/70 hover:text-white hover:bg-white/5">
            <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2 text-white">
              <Palette className="w-7 h-7" style={{ color: '#5BB8E8' }} /> White-Label · {tenant?.name}
            </h1>
            <p className="text-white/60 mt-1">Previsualiza como o produto vai aparecer com o branding de <b className="text-white/80">{tenant?.slug}</b></p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 hover:text-white" onClick={resetDefaults}>
            Reset defaults
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="text-white font-semibold"
            style={{ background: '#2196C9' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#5BB8E8')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#2196C9')}
            data-testid="sa-branding-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Guardar branding</>}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <Card className="bg-[#0D1B3E]/60 border-white/10 backdrop-blur-sm">
          <CardHeader><CardTitle className="text-white">Configuração</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-white/80">Logo (max 500KB)</Label>
              <div className="mt-2 flex items-center gap-3">
                {branding.logo_base64 ? (
                  <img src={branding.logo_base64} alt="logo" className="w-16 h-16 rounded-lg object-contain bg-[#0a0e1a] p-2 border border-white/10" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-[#0a0e1a] border border-white/10 flex items-center justify-center text-white/40 text-xs">sem logo</div>
                )}
                <label className="flex-1 cursor-pointer">
                  <div className="border-2 border-dashed border-white/15 rounded-lg p-4 text-center text-sm text-white/60 hover:border-white/30 transition-colors">
                    <Upload className="w-5 h-5 mx-auto mb-1" /> Escolher ficheiro
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="sa-branding-logo-input" />
                </label>
              </div>
            </div>
            <ColorInput label="Cor primária" value={branding.primary_color} onChange={(v) => patch('primary_color', v)} testid="sa-branding-primary" />
            <ColorInput label="Cor de destaque" value={branding.accent_color} onChange={(v) => patch('accent_color', v)} testid="sa-branding-accent" />
            <ColorInput label="Fundo" value={branding.background_color} onChange={(v) => patch('background_color', v)} testid="sa-branding-background" />
            <div>
              <Label className="text-white/80">Fonte</Label>
              <Select value={branding.font_family} onValueChange={(v) => patch('font_family', v)}>
                <SelectTrigger className="bg-[#0a0e1a] border-white/10 mt-1 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>{FONTS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80">Tagline</Label>
              <Input value={branding.tagline} onChange={(e) => patch('tagline', e.target.value)} className="bg-[#0a0e1a] border-white/10 text-white mt-1" data-testid="sa-branding-tagline" />
            </div>
            <div>
              <Label className="text-white/80">Domínio custom (opcional)</Label>
              <Input value={branding.custom_domain} onChange={(e) => patch('custom_domain', e.target.value)} placeholder="compliance.acme.pt" className="bg-[#0a0e1a] border-white/10 text-white mt-1" />
              <p className="text-xs text-white/50 mt-1">Requer configuração DNS + certificado SSL do lado da Emergent.</p>
            </div>
          </CardContent>
        </Card>

        {/* Live preview */}
        <Card className="bg-[#0D1B3E]/60 border-white/10 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5" style={{ color: '#5BB8E8' }} /> Preview em tempo real
            </CardTitle>
            <p className="text-xs text-white/50 mt-1">O que o cliente vê ao entrar no produto</p>
          </CardHeader>
          <CardContent>
            <div
              className="rounded-lg overflow-hidden border-2 border-white/10"
              style={{ background: branding.background_color, fontFamily: branding.font_family, color: '#f1f5f9' }}
              data-testid="sa-branding-preview"
            >
              {/* Mock header */}
              <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: `${branding.primary_color}30`, background: `${branding.primary_color}15` }}>
                <div className="flex items-center gap-3">
                  {branding.logo_base64 ? (
                    <img src={branding.logo_base64} alt="" className="w-10 h-10 rounded object-contain" />
                  ) : (
                    <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: branding.primary_color }}>
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div>
                    <div className="font-bold" style={{ color: branding.primary_color }}>{tenant?.name}</div>
                    <div className="text-xs opacity-70">{branding.tagline}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: branding.accent_color }} />
                  <div className="w-2 h-2 rounded-full opacity-50" style={{ background: branding.accent_color }} />
                </div>
              </div>
              {/* Mock KPI cards */}
              <div className="p-5 grid grid-cols-2 gap-3">
                <div className="p-4 rounded-lg" style={{ background: `${branding.primary_color}20` }}>
                  <div className="text-2xl font-bold" style={{ color: branding.primary_color }}>87%</div>
                  <div className="text-xs opacity-70">Compliance score</div>
                </div>
                <div className="p-4 rounded-lg" style={{ background: `${branding.accent_color}20` }}>
                  <div className="text-2xl font-bold" style={{ color: branding.accent_color }}>12</div>
                  <div className="text-xs opacity-70">Tarefas pendentes</div>
                </div>
                <div className="col-span-2 p-4 rounded-lg" style={{ background: `${branding.primary_color}10` }}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm">Próxima revisão</span>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: branding.accent_color, color: '#000' }}>Em 5 dias</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div className="h-2 rounded-full" style={{ width: '65%', background: branding.primary_color }} />
                  </div>
                </div>
              </div>
              <div className="p-5 pt-0">
                <button className="w-full py-2.5 rounded-lg font-semibold" style={{ background: branding.primary_color }}>
                  Iniciar auditoria
                </button>
              </div>
            </div>

            <div className="mt-4 p-3 rounded-lg text-xs" style={{ background: 'rgba(33,150,201,0.1)', border: '1px solid rgba(91,184,232,0.3)', color: '#5BB8E8' }}>
              💡 <b>Ideia comercial</b>: white-label é ideal para pacotes Enterprise (€300-500/mês adicional). Podes usar este preview para fechar clientes exigentes.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ColorInput({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-white/80">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-14 rounded border border-white/10 bg-[#0a0e1a] cursor-pointer" data-testid={testid} />
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="#000000" className="bg-[#0a0e1a] border-white/10 text-white font-mono" />
      </div>
    </div>
  );
}
